import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, Box, Typography, IconButton, Button, Divider,
  List, ListItem, ListItemText, ListItemAvatar, Avatar,
  CircularProgress, Badge, Tooltip, Dialog
} from "@mui/material";
import { ShoppingCart, Close, Delete, CheckCircle, InfoOutlined, Inventory } from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext.jsx";

const API_URL = "https://apiweb.hitpoly.com/ajax/carritoController.php";

const getTipoServicio = (item) => {
  if (item.tipo_servicio) return item.tipo_servicio;
  return item.servicio_nombre ? "empresa" : "profesional";
};

const TipoServicioBadge = ({ item }) => {
  const esEmpresa = getTipoServicio(item) === "empresa";
  return (
    <Typography
      variant="caption"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        mt: 0.5,
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        fontSize: "0.6rem",
        fontWeight: 600,
        lineHeight: 1.2,
        color: esEmpresa ? "#0b8db5" : "#3B207F",
        bgcolor: esEmpresa ? "#0b8db520" : "#3B207F15",
      }}
    >
      {esEmpresa ? "Servicio de Empresa" : "Servicio de Profesional"}
    </Typography>
  );
};


const CarritoDrawer = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, itemId: null, itemName: "" });
  const [detailItem, setDetailItem] = useState(null);
  
  // Auto-abrir si viene de academia
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cart") === "open") {
      setOpen(true);
      // Limpiar URL para evitar reaperturas al recargar
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
    
    // Escuchar evento personalizado para abrir desde otras partes de la app o iframe
    const handleOpenEvent = () => {
      setOpen(true);
    };
    window.addEventListener('open-cart-drawer', handleOpenEvent);
    return () => window.removeEventListener('open-cart-drawer', handleOpenEvent);
  }, []);

  const fetchItems = useCallback(async () => {
    const userId = user?.id || user?.user_id;
    if (!isAuthenticated || !userId) {
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ accion: "getItems", usuario_id: userId }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setItems(data.items || []);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const handleRemove = async (itemId) => {
    setRemovingId(itemId);
    const userId = user?.id || user?.user_id;
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ accion: "removeItem", usuario_id: userId, item_id: itemId }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setItems((prev) => prev.filter((i) => i.id !== itemId));
      }
    } catch (e) {
    } finally {
      setRemovingId(null);
      setDeleteConfirm({ open: false, itemId: null, itemName: "" });
    }
  };

  const openDeleteConfirm = (item) => {
    setDeleteConfirm({ open: true, itemId: item.id, itemName: item.nombre });
  };

  const totalFinal = (items || []).reduce((acc, i) => acc + parseFloat(i.precio_final || 0), 0);

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Icono flotante del carrito */}
      <Tooltip title="Mi Carrito">
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            backgroundColor: "white",
            borderRadius: "50%",
            padding: 1,
            marginRight: 1,
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <Badge badgeContent={(items || []).length || null} color="error">
            <ShoppingCart sx={{ color: "#F21C63", fontSize: "1.5rem" }} />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Drawer lateral */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 420 },
            display: "flex",
            flexDirection: "column",
            zIndex: 200000, // Por encima de la navbar (100,000)
          },
        }}
        sx={{ zIndex: 200000 }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1a0533 0%, #F21C63 100%)",
            p: 2.5,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ShoppingCart />
            <Typography variant="h6" fontWeight="bold">
              Mi Carrito
            </Typography>
            <Typography
              variant="caption"
              sx={{ bgcolor: "rgba(255,255,255,0.2)", px: 1.2, py: 0.3, borderRadius: 10 }}
            >
              {(items || []).length} {(items || []).length === 1 ? "producto" : "productos"}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>

        {/* Contenido */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <CircularProgress sx={{ color: "#F21C63" }} />
            </Box>
          ) : (items || []).length === 0 ? (
            <Box sx={{ textAlign: "center", mt: 8, px: 3 }}>
              <ShoppingCart sx={{ fontSize: 64, color: "#e0e0e0", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Tu carrito está vacío
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Agrega cursos desde la Academia Hitpoly
              </Typography>
              <Button
                variant="contained"
                href="http://localhost:3001"
                sx={{ mt: 3, bgcolor: "#F21C63", "&:hover": { bgcolor: "#d41857" } }}
              >
                Explorar Cursos
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {(items || []).map((item) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    alignItems="center"
                    sx={{
                      px: 0,
                      py: 2,
                      gap: 2,
                      cursor: "pointer",
                      borderRadius: 2,
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                    onClick={() => setDetailItem(item)}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={(e) => { e.stopPropagation(); openDeleteConfirm(item); }}
                        disabled={removingId === item.id}
                        size="small"
                        sx={{ 
                          color: "#999",
                          "&:hover": { color: "#F21C63" }
                        }}
                      >
                        {removingId === item.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Delete fontSize="small" />
                        )}
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={item.imagen}
                        variant="rounded"
                        sx={{ width: 80, height: 48, borderRadius: 1.5 }}
                        imgProps={{ style: { objectFit: 'cover' } }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ pr: 5 }} // Más espacio para evitar el botón de eliminar
                      primary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            sx={{ 
                              lineHeight: 1.2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: "0.85rem"
                            }}
                          >
                            {item.nombre || "Sin nombre"}
                          </Typography>
                          <TipoServicioBadge item={item} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                          {parseFloat(item.precio_original) > parseFloat(item.precio_final) && (
                            <Typography
                              variant="caption"
                              sx={{ textDecoration: "line-through", color: "#bbb" }}
                            >
                              ${parseFloat(item.precio_original).toFixed(2)}
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight="bold" color="#F21C63">
                            ${parseFloat(item.precio_final).toFixed(2)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer con total */}
        {(items || []).length > 0 && (
          <Box
            sx={{
              p: 2.5,
              borderTop: "1px solid #e0e0e0",
              bgcolor: "#fafafa",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Total:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="#F21C63">
                ${totalFinal.toFixed(2)}
              </Typography>
            </Box>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<CheckCircle />}
              sx={{
                bgcolor: "#F21C63",
                "&:hover": { bgcolor: "#d41857" },
                borderRadius: 2,
                py: 1.5,
                fontWeight: "bold",
                fontSize: "1rem",
              }}
            >
              Proceder al Pago
            </Button>
            <Button
              fullWidth
              variant="text"
              sx={{ mt: 1, color: "text.secondary" }}
              onClick={() => {
                setOpen(false);
              }}
            >
              Seguir explorando
            </Button>
          </Box>
        )}
      </Drawer>

      {/* Diálogo de Detalle del Item */}
      <Dialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 300000 }}
      >
        {detailItem && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Detalle del Producto</Typography>
              <IconButton size="small" onClick={() => setDetailItem(null)}><Close /></IconButton>
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Avatar
                src={detailItem.imagen}
                variant="rounded"
                sx={{ width: 100, height: 100, borderRadius: 2 }}
                imgProps={{ style: { objectFit: 'cover' } }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {detailItem.nombre || "Sin nombre"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  {parseFloat(detailItem.precio_original) > parseFloat(detailItem.precio_final) && (
                    <Typography variant="body2" sx={{ textDecoration: "line-through", color: "#bbb" }}>
                      ${parseFloat(detailItem.precio_original).toFixed(2)}
                    </Typography>
                  )}
                  <Typography variant="h6" fontWeight="bold" color="#F21C63">
                    ${parseFloat(detailItem.precio_final).toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Origen: {detailItem.origen === "academy" ? "Hitpoly Academy" : detailItem.origen || "Servicios"}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Tipo de servicio: {getTipoServicio(detailItem) === "empresa" ? "Servicio de Empresa" : "Servicio de Profesional"}
                </Typography>
                {detailItem.tipo_contrato && (
                  <Typography variant="caption" color="text.secondary">
                    Tipo: {detailItem.tipo_contrato}
                  </Typography>
                )}
              </Box>
            </Box>
            {detailItem.descripcion && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                  {detailItem.descripcion}
                </Typography>
              </>
            )}
            {detailItem.beneficios && Array.isArray(detailItem.beneficios) && detailItem.beneficios.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Inventory sx={{ fontSize: 18, color: "#0b8db5" }} /> Beneficios incluidos
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {detailItem.beneficios.map((b, i) => (
                    <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>
                      {typeof b === 'string' ? b : b?.titulo || b?.descripcion || ''}
                    </Typography>
                  ))}
                </Box>
              </>
            )}
            <Box sx={{ mt: 3, display: "flex", gap: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  setDetailItem(null);
                  openDeleteConfirm(detailItem);
                }}
              >
                Eliminar
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setDetailItem(null)}
                sx={{ bgcolor: "#0b8db5", "&:hover": { bgcolor: "#0a7aa0" } }}
              >
                Cerrar
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ ...deleteConfirm, open: false })}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        sx={{ zIndex: 300000 }} // Mayor que el Drawer (200000)
      >
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Delete sx={{ fontSize: 48, color: "#F21C63", mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            ¿Eliminar del carrito?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Estás a punto de eliminar <strong>{deleteConfirm.itemName}</strong> de tu carrito de compras.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteConfirm({ ...deleteConfirm, open: false })}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleRemove(deleteConfirm.itemId)}
              disabled={removingId !== null}
              sx={{
                bgcolor: "#F21C63",
                "&:hover": { bgcolor: "#d41857" },
                borderRadius: 2,
                px: 3,
              }}
            >
              {removingId !== null ? <CircularProgress size={20} color="inherit" /> : "Sí, eliminar"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default CarritoDrawer;
